
import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { User, Company, UserRole, Tenant } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import Spinner from '../../components/common/Spinner';
import SuperAdminUserModal from '../../components/admin/SuperAdminUserModal';
import { AuthContext } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';

const SuperAdminUsersPage: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const authContext = useContext(AuthContext);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [fetchedUsers, fetchedTenants, fetchedCompanies] = await Promise.all([
                firestoreService.getAllUsers(),
                firestoreService.getTenants(),
                firestoreService.getAllCompanies()
            ]);
            setUsers(fetchedUsers);
            setTenants(fetchedTenants);
            setCompanies(fetchedCompanies);
        } catch (err) {
            console.error("Failed to load platform data:", err);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const tenantMap = useMemo(() => {
        return tenants.reduce((map, t) => {
            map[t.tenantId] = t.name;
            return map;
        }, {} as Record<string, string>);
    }, [tenants]);

    const companyMap = useMemo(() => {
        return companies.reduce((map, company) => {
            map[company.companyId] = company.name;
            return map;
        }, {} as Record<string, string>);
    }, [companies]);

    const handleOpenModal = (user: User | null) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingUser(null);
        setIsModalOpen(false);
    };

    const handleSaveUser = async (userData: Omit<User, 'uid' | 'createdAt'>, password?: string) => {
        try {
            if (editingUser) {
                await firestoreService.updateUser(editingUser.uid, userData);
            } else if (password) {
                await firestoreService.registerUser(userData, password);
            }
            fetchData();
        } catch (error: any) {
            throw error;
        }
    };

    // Fix: Added missing handleDeleteClick function
    const handleDeleteClick = (user: User) => {
        setUserToDelete(user);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;
        setIsDeleting(true);
        try {
            await firestoreService.deleteUser(userToDelete.uid);
            fetchData();
            setUserToDelete(null);
        } catch (error: any) {
            alert(`Error al eliminar: ${error.message}`);
        } finally {
            setIsDeleting(false);
        }
    };
    
    const formatRole = (role: string) => role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    if (authContext?.user?.role !== UserRole.PLATFORM_OWNER) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="container mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-white">Todos los Usuarios de la Plataforma</h1>
                <button 
                    onClick={() => handleOpenModal(null)}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg">
                    Crear Usuario de Agencia
                </button>
            </div>

            <div className="bg-gray-900 rounded-xl shadow-xl overflow-hidden border border-gray-800">
                {loading ? (
                    <div className="p-20 text-center"><Spinner size="lg" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-800/50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Nombre</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Agencia (Tenant)</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Rol</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Estado</th>
                                <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Acciones</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                            {users.map((user) => (
                                <tr key={user.uid} className="hover:bg-gray-800/40 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{user.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className="font-semibold text-blue-400">{tenantMap[user.tenantId] || 'Plataforma'}</span>
                                    {user.companyId && <span className="block text-xs text-gray-500">{companyMap[user.companyId]}</span>}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-xs uppercase font-bold">{formatRole(user.role)}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${ user.active ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300' }`}>
                                    {user.active ? 'Activo' : 'Inactivo'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                                    <button onClick={() => handleOpenModal(user)} className="text-blue-400 hover:text-blue-300">Editar</button>
                                    <button onClick={() => handleDeleteClick(user)} className="text-red-400 hover:text-red-300">Eliminar</button>
                                </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {isModalOpen && (
                <SuperAdminUserModal 
                    userToEdit={editingUser}
                    tenants={tenants}
                    onClose={handleCloseModal}
                    onSave={handleSaveUser}
                />
            )}
            <ConfirmationModal
                isOpen={!!userToDelete}
                onClose={() => setUserToDelete(null)}
                onConfirm={confirmDeleteUser}
                title="Eliminar Usuario"
                message={<>¿Estás seguro de que deseas eliminar permanentemente a <strong>{userToDelete?.name}</strong>?</>}
                isConfirming={isDeleting}
            />
        </div>
    );
};
export default SuperAdminUsersPage;
